import {
  DashboardIcon,
  FolderIcon,
  IbanIcon,
  SettingsCogIcon,
} from "@volt_developers/components";
import { uniqueId } from "lodash";

import { CircuitBreakerOutlinedIcon } from "assets/productIcons";
import { COMMON_TRANSLATION_KEYS } from "constants/commonTranslationsKeys";
import { ACTION_LIST } from "models/role/action";
import {
  SUBJECT_ACCOUNT,
  SUBJECT_BANK,
  SUBJECT_CIRCUIT_BREAKER,
  SUBJECT_CLIENT,
  SUBJECT_CUSTOMER,
  SUBJECT_PAYMENT,
  SUBJECT_USER,
} from "models/role/subject";
import { applicationRoutes } from "modules/applications";
import { accountsRoutes } from "modules/bank-accounts";
import { bankRoutes } from "modules/banks";
import {
  allowlistRoute,
  blocklistRoute,
  circuitBreakerRoutes,
  riskRulesRoute,
} from "modules/circuit-breaker";
import { customersRoutes } from "modules/customers";
import { dashboardRoutes } from "modules/dashboard";
import { transactionsRoutes } from "modules/transactions";
import { userManagementRoutes } from "modules/user-management";
import { configurationRoutes } from "routes/configuration";

import { SIDEBAR_TEST_IDS } from "../../constants";

import type { ReactNode } from "react";
import type { PermissionObject } from "../../types";

export interface SidebarConfigItem {
  id: string;
  title: string;
  permissions: PermissionObject[];
  permissionOneOf?: boolean;
  url?: string;
  children?: SidebarConfigItem[];
  isActive?: (path: string, activePath: string) => boolean;
  icon?: ReactNode;
  "data-testid"?: string;
}

export const sidebarConfig: SidebarConfigItem[] = [
  {
    id: uniqueId("sidebar_"),
    title: COMMON_TRANSLATION_KEYS.Dashboard,
    url: dashboardRoutes.path,
    isActive,
    icon: <DashboardIcon />,
    "data-testid": SIDEBAR_TEST_IDS.DASHBOARD,
    permissions: [],
  },
  {
    id: uniqueId("sidebar_"),
    title: COMMON_TRANSLATION_KEYS.payments,
    url: transactionsRoutes.path,
    isActive,
    icon: <FolderIcon />,
    "data-testid": SIDEBAR_TEST_IDS.PAYMENTS,
    permissions: [
      {
        do: ACTION_LIST,
        on: SUBJECT_PAYMENT,
      },
    ],
  },
  // https://supercharged.atlassian.net/browse/MP-1955
  // {
  //   id: uniqueId("sidebar_"),
  //   title: COMMON_TRANSLATION_KEYS.connect,
  //   url: "/connect",
  //   icon: <ConnectIcon />,
  //   "data-testid": SIDEBAR_TEST_IDS.CONNECT,
  //   permissions: [
  //     {
  //       do: ACTION_LIST,
  //       on: SUBJECT_CASH_MANAGEMENT,
  //     },
  //   ],
  // },
  // {
  //   id: uniqueId("sidebar_"),
  //   title: COMMON_TRANSLATION_KEYS.checkoutBranding,
  //   url: "/checkout-branding",
  //   isActive,
  //   icon: <CheckoutIcon />,
  //   "data-testid": SIDEBAR_TEST_IDS.CHECKOUT_BRANDING,
  //   permissions: [
  //     {
  //       do: ACTION_LIST,
  //       on: SUBJECT_CUSTOMER,
  //     },
  //   ],
  // },
  {
    id: uniqueId("sidebar_"),
    title: COMMON_TRANSLATION_KEYS.circuitBreaker,
    icon: <CircuitBreakerOutlinedIcon />,
    url: circuitBreakerRoutes.path,
    isActive,
    "data-testid": SIDEBAR_TEST_IDS.CIRCUIT_BREAKER,
    permissions: [
      {
        do: ACTION_LIST,
        on: SUBJECT_CIRCUIT_BREAKER,
      },
    ],
    children: [
      {
        id: uniqueId("sidebar_"),
        title: COMMON_TRANSLATION_KEYS.allowlist,
        url: allowlistRoute.path,
        isActive,
        "data-testid": SIDEBAR_TEST_IDS.ALLOWLIST,
        permissions: [
          {
            do: ACTION_LIST,
            on: SUBJECT_CIRCUIT_BREAKER,
          },
        ],
      },
      {
        id: uniqueId("sidebar_"),
        title: COMMON_TRANSLATION_KEYS.blocklist,
        url: blocklistRoute.path,
        isActive,
        "data-testid": SIDEBAR_TEST_IDS.BLOCKLIST,
        permissions: [
          {
            do: ACTION_LIST,
            on: SUBJECT_CIRCUIT_BREAKER,
          },
        ],
      },
      {
        id: uniqueId("sidebar_"),
        title: COMMON_TRANSLATION_KEYS.riskRules,
        url: riskRulesRoute.path,
        isActive,
        "data-testid": SIDEBAR_TEST_IDS.RISK_RULES,
        permissions: [
          {
            do: ACTION_LIST,
            on: SUBJECT_CIRCUIT_BREAKER,
          },
        ],
      },
    ],
  },
  {
    id: uniqueId("sidebar_"),
    title: COMMON_TRANSLATION_KEYS.configuration,
    icon: <SettingsCogIcon />,
    url: configurationRoutes.path,
    isActive,
    "data-testid": SIDEBAR_TEST_IDS.CONFIGURATION,
    permissions: [
      {
        do: ACTION_LIST,
        on: SUBJECT_CLIENT,
      },
      {
        do: ACTION_LIST,
        on: SUBJECT_ACCOUNT,
      },
      {
        do: ACTION_LIST,
        on: SUBJECT_CUSTOMER,
      },
      {
        do: ACTION_LIST,
        on: SUBJECT_USER,
      },
    ],
    permissionOneOf: true,
    children: [
      {
        id: uniqueId("sidebar_"),
        title: COMMON_TRANSLATION_KEYS.Applications,
        url: applicationRoutes.path,
        isActive,
        "data-testid": SIDEBAR_TEST_IDS.APPLICATIONS,
        permissions: [
          {
            do: ACTION_LIST,
            on: SUBJECT_CLIENT,
          },
        ],
      },
      {
        id: uniqueId("sidebar_"),
        title: COMMON_TRANSLATION_KEYS["Bank accounts"],
        url: accountsRoutes.path,
        isActive,
        "data-testid": SIDEBAR_TEST_IDS.ACCOUNTS,
        permissions: [
          {
            do: ACTION_LIST,
            on: SUBJECT_ACCOUNT,
          },
        ],
      },
      {
        id: uniqueId("sidebar_"),
        title: COMMON_TRANSLATION_KEYS.customers,
        url: customersRoutes.path,
        isActive,
        "data-testid": SIDEBAR_TEST_IDS.CUSTOMERS,
        permissions: [
          {
            do: ACTION_LIST,
            on: SUBJECT_CUSTOMER,
          },
        ],
      },
      {
        id: uniqueId("sidebar_"),
        title: COMMON_TRANSLATION_KEYS.Users,
        url: userManagementRoutes.path,
        isActive,
        "data-testid": SIDEBAR_TEST_IDS.USERS,
        permissions: [
          {
            do: ACTION_LIST,
            on: SUBJECT_USER,
          },
        ],
      },
    ],
  },
  {
    id: uniqueId("sidebar_"),
    title: COMMON_TRANSLATION_KEYS.banks,
    url: bankRoutes.path,
    isActive,
    icon: <IbanIcon />,
    "data-testid": SIDEBAR_TEST_IDS.BANKS,
    permissions: [
      {
        do: ACTION_LIST,
        on: SUBJECT_BANK,
      },
    ],
  },
];

function isActive(path: string, activePath: string) {
  if (path === "/") {
    return path === activePath;
  }

  return activePath.startsWith(path);
}
